import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, PlayCircle, Sparkles, Volume2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import AppLayout from "@/components/AppLayout";
import BilingualText from "@/components/BilingualText";
import HoverText from "@/components/HoverWord";
import SessionSummary from "@/components/SessionSummary";
import SpeakButton from "@/components/SpeakButton";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { addGuestAttempt } from "@/lib/localProgress";
import { IDS } from "@/constants/testIds";
import { cn as cx } from "@/lib/utils";

const SKILL_TITLES = {
  reading: { cn: "阅读理解", en: "Reading Comprehension" },
  vocabulary: { cn: "词汇练习", en: "Vocabulary Practice" },
  grammar: { cn: "语法练习", en: "Grammar Practice" },
  listening: { cn: "听力练习", en: "Listening Practice" },
};

const SESSION_LENGTH = 5;

export default function PracticeMCQ() {
  const { skill } = useParams();
  const nav = useNavigate();
  const { currentProfile, isAuthed } = useAuth();
  const level = currentProfile?.level || "A2";

  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [choice, setChoice] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [explaining, setExplaining] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [sessionResults, setSessionResults] = useState([]); // [{correct: bool}] × N
  const [sessionDone, setSessionDone] = useState(false);
  const [voice, setVoice] = useState("fable"); // 'fable' = British male, 'alloy' = American neutral, 'shimmer' = American female
  const audioRef = useRef(null);

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/questions", {
        params: { skill, level, limit: 10 },
      });
      setQuestions(data);
      setIdx(0);
      setChoice(null);
      setSubmitted(false);
      setExplanation(null);
      setAudioUrl(null);
      setShowTranscript(false);
      setSessionResults([]);
      setSessionDone(false);
    } catch {
      toast.error("Could not load questions.");
    } finally {
      setLoading(false);
    }
  }, [skill, level]);

  useEffect(() => { loadQuestions(); }, [loadQuestions]);

  useEffect(() => {
    // Reset per-question state when moving to a new question.
    setChoice(null);
    setSubmitted(false);
    setExplanation(null);
    setShowTranscript(false);
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [idx]);

  const current = questions[idx];

  const submit = async () => {
    if (!current || choice === null) return;
    setSubmitted(true);
    const isCorrect = current.options?.[choice] === current.correct;
    setSessionResults((prev) => [...prev, { correct: isCorrect }]);

    if (isAuthed) {
      try {
        await api.post("/attempts", {
          question_id: current.id,
          student_answer: current.options?.[choice],
        });
      } catch { /* silent */ }
    } else {
      addGuestAttempt({
        skill: current.skill,
        level: current.level,
        question_id: current.id,
        student_answer: current.options?.[choice],
        correct: isCorrect,
        score: isCorrect ? 100 : 0,
      });
    }

    if (isCorrect) toast.success("对了！Correct!");
    else toast.error("再想想。Try again next time.");
  };

  const requestExplanation = async () => {
    if (!current) return;
    setExplaining(true);
    try {
      const { data } = await api.post("/explain", {
        question: current.question_en,
        options: current.options || [],
        correct_answer: current.correct,
        student_answer: current.options?.[choice] ?? "",
        level: current.level,
        skill: current.skill,
      });
      setExplanation(data);
    } catch {
      toast.error("Could not fetch explanation.");
    } finally {
      setExplaining(false);
    }
  };

  const playAudio = async () => {
    if (!current?.transcript_en) return;
    if (audioUrl && audioRef.current) {
      audioRef.current.play();
      return;
    }
    setAudioLoading(true);
    try {
      const res = await api.post(
        "/tts",
        { text: current.transcript_en, voice },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      setAudioUrl(url);
      setTimeout(() => audioRef.current?.play(), 50);
    } catch {
      toast.error("Audio failed.");
    } finally {
      setAudioLoading(false);
    }
  };

  // If user picks a new voice after audio was already generated, invalidate the cached clip
  const changeVoice = (v) => {
    if (v === voice) return;
    setVoice(v);
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const generateMore = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/questions/generate", {
        skill, level, topic: "mixed", count: 3,
      });
      if (data?.length) {
        setQuestions((prev) => [...prev, ...data]);
        toast.success(`+${data.length} new questions · 新增题目`);
      } else {
        toast.info("No new questions returned. Try again.");
      }
    } catch {
      toast.error("Generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const next = () => {
    // Check if session complete: hit SESSION_LENGTH answered questions
    if (sessionResults.length >= SESSION_LENGTH) {
      setSessionDone(true);
      return;
    }
    if (idx + 1 < questions.length) setIdx(idx + 1);
    else loadQuestions();
  };

  const restart = () => {
    loadQuestions();
  };

  const title = SKILL_TITLES[skill] || { cn: skill, en: skill };
  const progress = questions.length
    ? Math.min(100, (sessionResults.length / SESSION_LENGTH) * 100)
    : 0;

  // Session summary screen
  if (sessionDone) {
    const correctCount = sessionResults.filter((r) => r.correct).length;
    return (
      <AppLayout>
        <div data-testid={IDS.practice.root} className="max-w-3xl mx-auto">
          <SessionSummary
            correct={correctCount}
            total={sessionResults.length}
            skill={title.en}
            level={level}
            onRestart={restart}
            onDashboard={() => nav("/dashboard")}
          />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div data-testid={IDS.practice.root} className="max-w-3xl mx-auto space-y-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="badge-soft mb-2">
              {level} · {sessionResults.length + 1}/{SESSION_LENGTH}
            </span>
            <BilingualText cn={title.cn} en={title.en} size="hero" />
          </div>
          <Button
            variant="outline"
            className="rounded-full font-bold"
            onClick={generateMore}
            disabled={generating}
            data-testid={IDS.practice.generate}
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
            )}
            <span className="cn mr-1">AI 生成</span>Generate more
          </Button>
        </div>

        <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-sky-400 to-emerald-400 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>

        {loading ? (
          <div className="card-plush p-10 grid place-items-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : !current ? (
          <div className="card-plush p-10 text-center">
            <BilingualText
              cn="没有更多题目了，试试 AI 生成新题目！"
              en="No more questions — try generating new ones!"
              size="md"
            />
          </div>
        ) : (
          <motion.article
            key={current.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="card-plush p-6 sm:p-8 space-y-6"
          >
            {/* Reading passage */}
            {current.passage_en && (
              <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="cn text-base sm:text-lg font-medium text-slate-800 leading-relaxed flex-1">
                    {current.passage_cn}
                  </div>
                  <SpeakButton
                    text={current.passage_en}
                    voice={voice}
                    variant="ghost"
                    size="sm"
                    label={{ cn: "朗读", en: "Listen" }}
                    testId="speak-passage"
                    className="shrink-0"
                  />
                </div>
                <div className="text-slate-700 leading-relaxed">
                  <HoverText text={current.passage_en} />
                </div>
                <p className="mt-3 text-xs text-slate-400">
                  <span className="cn">点按英文单词查看翻译</span> · Tap any English word for its meaning
                </p>
              </div>
            )}

            {/* Listening audio */}
            {current.transcript_en && (
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    size="lg"
                    onClick={playAudio}
                    disabled={audioLoading}
                    className="rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold"
                    data-testid={IDS.practice.playAudio}
                  >
                    {audioLoading ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <PlayCircle className="w-5 h-5 mr-2" />
                    )}
                    <span className="cn mr-1">播放</span>Play
                  </Button>
                  {audioUrl && (
                    <audio ref={audioRef} src={audioUrl} controls className="flex-1" />
                  )}
                </div>

                {/* Voice picker */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-amber-900/70">
                    <span className="cn mr-1">口音</span>· Accent:
                  </span>
                  {[
                    { key: "fable", cn: "英式", en: "British" },
                    { key: "alloy", cn: "美式 M", en: "American M" },
                    { key: "shimmer", cn: "美式 F", en: "American F" },
                  ].map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => changeVoice(v.key)}
                      data-testid={`voice-${v.key}`}
                      className={
                        "rounded-full px-3 py-1 text-xs font-bold border transition-colors " +
                        (voice === v.key
                          ? "bg-amber-500 border-amber-500 text-white"
                          : "bg-white border-amber-200 text-amber-900 hover:bg-amber-100")
                      }
                    >
                      <span className="cn mr-1">{v.cn}</span>· {v.en}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => setShowTranscript((v) => !v)}
                  className="mt-3 text-sm font-bold text-amber-800 hover:underline"
                >
                  <Volume2 className="w-3.5 h-3.5 inline-block mr-1" />
                  <span className="cn mr-1">显示原文</span>
                  {showTranscript ? "Hide transcript" : "Show transcript"}
                </button>
                {showTranscript && (
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="cn text-slate-800">{current.transcript_cn}</div>
                    <div className="text-slate-600">
                      <HoverText text={current.transcript_en} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Question */}
            <div className="flex items-start justify-between gap-3">
              <BilingualText
                cn={current.question_cn}
                en={current.question_en}
                size="lg"
                className="flex-1"
              />
              <SpeakButton
                text={
                  (current.question_en || "") +
                  ((current.options || []).length
                    ? ". Options: " +
                      current.options
                        .map((o, i) => `${String.fromCharCode(65 + i)}. ${o}`)
                        .join(", ") +
                      "."
                    : "")
                }
                voice={voice}
                variant="default"
                size="md"
                label={{ cn: "朗读题目", en: "Listen" }}
                testId="speak-question"
                className="shrink-0 mt-1"
              />
            </div>

            {/* Options */}
            <div className="grid gap-3">
              {(current.options || []).map((opt, i) => {
                const isChosen = choice === i;
                const isCorrect = submitted && opt === current.correct;
                const isWrong = submitted && isChosen && opt !== current.correct;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => !submitted && setChoice(i)}
                    disabled={submitted}
                    data-testid={IDS.practice.option(i)}
                    className={cx(
                      "text-left rounded-2xl px-5 py-4 border-2 transition-all font-semibold",
                      isCorrect
                        ? "bg-emerald-50 border-emerald-400 text-emerald-900"
                        : isWrong
                          ? "bg-rose-50 border-rose-400 text-rose-900"
                          : isChosen
                            ? "bg-sky-50 border-sky-400 text-sky-900"
                            : "bg-white border-slate-200 hover:border-sky-300 hover:bg-sky-50/50",
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={cx(
                          "w-8 h-8 rounded-full grid place-items-center text-sm font-black shrink-0",
                          isCorrect
                            ? "bg-emerald-500 text-white"
                            : isWrong
                              ? "bg-rose-500 text-white"
                              : isChosen
                                ? "bg-sky-500 text-white"
                                : "bg-slate-100 text-slate-500",
                        )}
                      >
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span className="flex-1">{opt}</span>
                      {isCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                      {isWrong && <XCircle className="w-5 h-5 text-rose-500" />}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              {!submitted ? (
                <Button
                  size="lg"
                  className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
                  onClick={submit}
                  disabled={choice === null}
                  data-testid={IDS.practice.submit}
                >
                  <span className="cn mr-2">提交答案</span>Submit
                </Button>
              ) : (
                <>
                  <Button
                    size="lg"
                    className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
                    onClick={next}
                    data-testid={IDS.practice.next}
                  >
                    <span className="cn mr-2">下一题</span>Next →
                  </Button>
                  {!explanation && (
                    <Button
                      size="lg"
                      variant="outline"
                      className="rounded-full font-bold"
                      onClick={requestExplanation}
                      disabled={explaining}
                    >
                      {explaining ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
                      )}
                      <span className="cn mr-1">AI 解析</span>Explain
                    </Button>
                  )}
                </>
              )}
            </div>

            {/* Explanation */}
            {explanation && (
              <motion.div
                data-testid={IDS.practice.explanation}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl p-5 bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-100"
              >
                <div className="cn text-slate-900 font-medium leading-relaxed">
                  {explanation.cn}
                </div>
                <div className="mt-2 text-slate-700 leading-relaxed">
                  {explanation.en}
                </div>
                {(explanation.tip_cn || explanation.tip_en) && (
                  <div className="mt-4 pt-3 border-t border-white/60">
                    <div className="text-xs font-black text-amber-700 uppercase tracking-widest mb-1">
                      Tip · 小贴士
                    </div>
                    <div className="cn text-sm text-slate-800">{explanation.tip_cn}</div>
                    <div className="text-sm text-slate-600">{explanation.tip_en}</div>
                  </div>
                )}
              </motion.div>
            )}
          </motion.article>
        )}
      </div>
    </AppLayout>
  );
}
